import React, { useEffect, useRef, useState, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';

const NewSignature = (props) => {
    const signatureCanvasRef = useRef(null);
    const parentRef = useRef(null);
    const prevValueRef = useRef(props.value);
    const [parentWidth, setParentWidth] = useState(200);
    
    const { value, disabled, readOnly, required, label, validation, name } = props;
    const errorMessage = validation && name ? (validation[name] || '') : '';

    // Helper to check if canvas is empty
    const canvasIsEmpty = useCallback(() => {
        if (!signatureCanvasRef.current) return true;
        const canvas = signatureCanvasRef.current.getCanvas();
        return canvas.toDataURL() === canvas.toDataURL('image/png', 0.0);
    }, []);

    // Load signature image function
    const loadSignature = useCallback((sigValue, width) => {
        if (!signatureCanvasRef.current || !sigValue) return;
        
        const canvas = signatureCanvasRef.current.getCanvas();
        const targetWidth = width || parentWidth;
        
        if (targetWidth > 0 && Math.abs(canvas.width - targetWidth) > 1) {
            canvas.width = targetWidth;
            canvas.height = 70;
        }
        
        const img = new Image();
        img.onload = () => {
            if (signatureCanvasRef.current) {
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);
            }
        };
        img.onerror = () => console.warn('Failed to load signature');
        img.src = sigValue;
    }, [parentWidth]);

    // Calculate and update parent width with observers
    useEffect(() => {
        if (!parentRef.current) return;
        
        const calculateWidth = () => {
            if (parentRef.current) {
                const width = parentRef.current.clientWidth || 200;
                setParentWidth(prev => width !== prev ? width : prev);
            }
        };
        
        calculateWidth();
        const timeouts = [100, 500, 1000].map(delay => setTimeout(calculateWidth, delay));
        
        let resizeObserver = null;
        let intersectionObserver = null;
        
        if (window.ResizeObserver) {
            resizeObserver = new ResizeObserver(() => calculateWidth());
            resizeObserver.observe(parentRef.current);
        }
        
        if (window.IntersectionObserver) {
            intersectionObserver = new IntersectionObserver((entries) => {
                if (entries[0]?.isIntersecting) {
                    setTimeout(calculateWidth, 50);
                    // Load signature when component becomes visible (tab switch)
                    setTimeout(() => {
                        if (value && signatureCanvasRef.current && parentWidth > 0 && canvasIsEmpty()) {
                            loadSignature(value, parentWidth);
                        }
                    }, 150);
                }
            }, { threshold: 0.1 });
            intersectionObserver.observe(parentRef.current);
        }
        
        window.addEventListener('resize', calculateWidth);
        
        return () => {
            timeouts.forEach(clearTimeout);
            resizeObserver?.disconnect();
            intersectionObserver?.disconnect();
            window.removeEventListener('resize', calculateWidth);
        };
    }, [value, parentWidth, loadSignature, canvasIsEmpty]);

    // Update canvas dimensions when width changes (preserve content if no value prop)
    useEffect(() => {
        if (!signatureCanvasRef.current || parentWidth <= 0) return;
        
        const canvas = signatureCanvasRef.current.getCanvas();
        if (Math.abs(canvas.width - parentWidth) > 1) {
            const currentData = canvas.toDataURL();
            const hasContent = currentData && currentData !== 'data:,' && currentData.length > 22;
            
            canvas.width = parentWidth;
            canvas.height = 70;
            
            // Restore content only if no value prop (value effect will handle it)
            if (hasContent && !value) {
                const img = new Image();
                img.onload = () => canvas.getContext('2d').drawImage(img, 0, 0);
                img.src = currentData;
            }
        }
    }, [parentWidth, value]);

    // Load signature when value changes
    useEffect(() => {
        if (!signatureCanvasRef.current || parentWidth <= 0) return;
        
        const prevValue = prevValueRef.current;
        
        if (value && (value !== prevValue || canvasIsEmpty())) {
            prevValueRef.current = value;
            loadSignature(value, parentWidth);
        } else if (value === '' && prevValue) {
            prevValueRef.current = value;
            signatureCanvasRef.current.clear();
        }
    }, [value, parentWidth, loadSignature, canvasIsEmpty]);

    // 3. Clear functionality
    const clearSignature = () => {
        if (signatureCanvasRef.current) {
            signatureCanvasRef.current.clear();
        }
        props.changeHandler('');
    };

    // 4. Save functionality (triggered onEnd of drawing)
    const saveSignature = () => {
        if (signatureCanvasRef.current) {
            const signatureImage = signatureCanvasRef.current.toDataURL();
            props.changeHandler(signatureImage);
        }
    };

    return (
        <div className="form-group mb-0">
            {label ? <label className="form-label">{label}</label> : null}
             <div className="card">
                <div className="card-header bg-[#F0F3FD] underline">
                    Draw
                </div>
                <div className="card-body p-1">
                    <div className="input-group1 position-relative">
                        <div 
                            className="form-control border-0 p-0" 
                            ref={parentRef}
                            style={{ 
                                height: '100px', 
                                width: '100%', 
                                borderLeft: required ? '2px solid red' : 'none' 
                            }}
                        >
                            {(disabled === true || readOnly === true) ? (
                                 value ? (
                                    <img 
                                        src={value} 
                                        alt="Signature" 
                                        style={{ maxHeight: '70px', maxWidth: '100%' }} 
                                    />
                                ) : null
                            ) : (
                                <SignatureCanvas
                                    ref={signatureCanvasRef}
                                    penColor='black'
                                    minWidth={0.5}
                                    maxWidth={1.5}
                                    onEnd={saveSignature}
                                    canvasProps={{ 
                                        width: parentWidth, 
                                        height: 70, 
                                        className: 'signature-canvas',
                                        style: { 
                                            touchAction: 'none',
                                            display: 'block',
                                            width: `${parentWidth}px`,
                                            height: '70px'
                                        } 
                                    }}
                                />
                            )}
                        </div>

                        {/* Clear Button (Eraser Icon) - Only show if not disabled/readOnly */}
                        {!(disabled === true || readOnly === true) && (
                            <button 
                                type="button" 
                                className="text-xs btn btn-xs btn-icon mt-1 position-absolute bottom-0 start-0" 
                                onClick={clearSignature}
                                title="Clear Signature"
                                style={{ zIndex: 10, background: 'transparent', border: 'none' }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M17.0138 1.95312C16.642 1.95312 16.4236 2.1421 16.2767 2.2901L14.554 4.00311L13.7 3.25214C13.2292 2.78114 12.6085 2.54614 11.9914 2.54614C11.3743 2.54614 10.7237 2.8191 10.2528 3.2901L5.2951 8.2431C4.9051 8.6331 4.9218 9.27315 5.3119 9.66315C5.7019 10.0541 6.3536 10.0371 6.7437 9.64612L11.6865 4.69611C11.848 4.53411 12.0999 4.46214 12.296 4.66614L13.1271 5.4201L7.2564 11.2901C6.2644 12.2831 5.3522 13.6211 4.4785 15.1961C3.8098 16.4011 3.1911 17.6921 2.6682 18.9771C2.4854 19.4261 2 20.7261 2 20.9651C2 21.2031 2.0785 21.4261 2.2313 21.6021C2.2464 21.6191 2.5412 21.9441 3.0014 21.9531L16.9634 21.9451C17.515 21.9451 17.9622 21.4981 17.9622 20.9451C17.9622 20.3931 17.515 19.9451 16.9634 19.9451L7.9169 19.9471C8.224 19.7911 8.536 19.6261 8.834 19.4601C10.4066 18.5861 11.6916 17.6831 12.6821 16.6911L19.1795 10.2271L21.6765 7.69611C21.8183 7.55211 21.9983 7.40112 21.9988 6.94812C22.0012 4.97112 21.1944 3.76214 20.7023 3.25714C20.2102 2.75314 19.0115 1.95513 17.0138 1.95312ZM17.3803 3.99414C17.799 3.98114 18.8303 4.22309 19.2774 4.64709C19.7245 5.07209 19.9818 6.1751 19.9675 6.5921L18.7144 7.84412L16.0292 5.36511L17.3803 3.99414ZM14.603 6.7901L17.3123 9.24414L11.2516 15.2891C10.4105 16.1311 9.2548 16.9371 7.831 17.7291C6.872 18.2621 5.7942 18.7631 4.7628 19.2031C5.2017 18.1701 5.6938 17.1241 6.2264 16.1641C7.0174 14.7391 7.8596 13.5511 8.7001 12.7091L14.603 6.7901Z"
                                        fill="#71798B" 
                                    />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Display validation error message */}
            {errorMessage && (
                <span className="text-danger m-1" style={{ display: 'block', marginTop: '4px' }}>
                    {errorMessage}
                </span>
            )}
        </div>
    );
};

NewSignature.defaultProps = {
    disabled: false,
    readOnly: false,
    required: false,
    validation: {},
    name: ''
};

export default NewSignature;